import { useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import '../configuration.scss';
import CreatePaymentMethodModal from './CreatePaymentMethodModal';
import ConfirmModal from '../../../../components/ConfirmModal/ConfirmModal';
import usePermissionsConfiguration from 'permissions/modules/configuration/usePermissionsConfiguration';
import { useLanguage } from '../../../../context/LanguageContext';
import { normalizeSupportedLanguage } from '../../../../utils/createScopedI18n';
import { paymentsI18n } from './i18n';

const mockSummary = {
  smsCredits: 2450,
  smsDelta: 150,
  emailCredits: 8320,
  emailDelta: 420,
  waCredits: 1890,
  waDelta: -210,
  aiCredits: 5640,
  aiDelta: 320,
};

const mockMethods = [
  {
    id: 1,
    brand: 'Visa Crédito',
    last4: '4532',
    expires: '12/2025',
    holder: 'Grupo Defensa SpA',
    network: 'Kushki',
    status: 'active',
    default: true,
  },
  {
    id: 2,
    brand: 'Mastercard Débito',
    last4: '8976',
    expires: '08/2026',
    holder: 'Grupo Defensa SpA',
    network: 'RedCompra',
    status: 'active',
    default: false,
  },
];

const subscriptionConfig = {
  price: 89990,
  currency: 'CLP',
  nextCharge: '2025-12-28',
  billingEmail: 'facturacion@grupodefensa.cl',
};

const localeByLanguage = {
  esCL: 'es-CL',
  enUS: 'en-US',
  ptBR: 'pt-BR',
};

const normalizeMethodStatus = (status) => {
  const normalized = String(status || '')
    .trim()
    .toLowerCase();

  if (normalized === 'inactive' || normalized === 'inactiva') {
    return 'inactive';
  }

  return 'active';
};

export default function Payments() {
  const { language } = useLanguage();
  const paymentsLanguage = normalizeSupportedLanguage(language);
  const { t } = useTranslation(undefined, { i18n: paymentsI18n });
  const { DesignSystemComponents } = useOutletContext() ?? {};
  const { Card, Button, SvgIcon, IconButton, Modal, Input, Dropdown } =
    DesignSystemComponents ?? {};

  const {
    canSetDefaultPayment,
    canAddPaymentMethod,
    canEditPaymentMethod,
    canDeletePaymentMethod,
    canChangePlan,
    canCancelSubscription,
  } = usePermissionsConfiguration();

  const [paymentMethods, setPaymentMethods] = useState(mockMethods);
  const [showMethodModal, setShowMethodModal] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [mode, setMode] = useState('auto');
  const [showDelete, setShowDelete] = useState(false);

  useEffect(() => {
    paymentsI18n.changeLanguage(paymentsLanguage);
  }, [paymentsLanguage]);

  const locale = localeByLanguage[paymentsLanguage] || 'es-CL';

  const summaryCards = useMemo(
    () => [
      {
        key: 'sms',
        icon: 'phone',
        label: t('payments_credits_sms'),
        value: mockSummary.smsCredits,
        delta: mockSummary.smsDelta,
      },
      {
        key: 'email',
        icon: 'send',
        label: t('payments_credits_email'),
        value: mockSummary.emailCredits,
        delta: mockSummary.emailDelta,
      },
      {
        key: 'whatsapp',
        icon: 'whatsapp',
        label: t('payments_credits_whatsapp'),
        value: mockSummary.waCredits,
        delta: mockSummary.waDelta,
      },
      {
        key: 'ai',
        icon: 'database',
        label: t('payments_credits_ai'),
        value: mockSummary.aiCredits,
        delta: mockSummary.aiDelta,
      },
    ],
    [t]
  );

  const subscription = useMemo(
    () => ({
      planName: t('payments_plan_enterprise'),
      price: subscriptionConfig.price,
      currency: subscriptionConfig.currency,
      nextCharge: new Date(
        `${subscriptionConfig.nextCharge}T00:00:00`
      ).toLocaleDateString(locale, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      }),
      status: 'active',
      billingPeriod: t('payments_billing_period_monthly'),
      billingEmail: subscriptionConfig.billingEmail,
    }),
    [locale, t]
  );

  const formatCurrency = (value) =>
    new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'CLP',
      maximumFractionDigits: 0,
    }).format(value);

  const formatNumber = (value) => Number(value || 0).toLocaleString(locale);

  const formatDelta = (value) => {
    const isPositive = value >= 0;
    return {
      prefix: isPositive ? t('payments_trend_up') : t('payments_trend_down'),
      colorClass: isPositive ? 'font-success' : 'font-error',
      amount: Math.abs(value).toLocaleString(locale),
    };
  };

  const handleAddMethod = () => {
    setSelectedMethod(null);
    setMode('create');
    setShowMethodModal(true);
  };

  const handleEditMethod = (method) => {
    setSelectedMethod(method);
    setMode('edit');
    setShowMethodModal(true);
  };

  const handleSaveMethod = (payload) => {
    const normalizedPayload = {
      ...payload,
      status: normalizeMethodStatus(payload.status),
    };

    if (normalizedPayload.id) {
      setPaymentMethods((prev) =>
        prev.map((method) =>
          method.id === normalizedPayload.id
            ? { ...method, ...normalizedPayload }
            : method
        )
      );
    } else {
      const nextId =
        paymentMethods.reduce(
          (maxValue, method) => (method.id > maxValue ? method.id : maxValue),
          0
        ) + 1;
      setPaymentMethods((prev) => [
        ...prev,
        { ...normalizedPayload, id: nextId },
      ]);
    }

    setShowMethodModal(false);
    setSelectedMethod(null);
    setMode('auto');
  };

  const handleDeleteMethod = () => {
    if (!selectedMethod) return;
    setPaymentMethods((prev) =>
      prev.filter((method) => method.id !== selectedMethod.id)
    );
    setShowDelete(false);
    setSelectedMethod(null);
  };

  if (!Card || !Button || !SvgIcon || !IconButton) return null;

  return (
    <div className='configuration-page gap-4 payments-page'>
      <div className='row mb-3'>
        <div className='col-12 mb-3'>
          <h2 className='mb-1'>{t('payments_title')}</h2>
          <p className='caption font-light'>{t('payments_subtitle')}</p>
        </div>
      </div>

      <div className='row mb-3 g-3'>
        {summaryCards.map((card) => {
          const delta = formatDelta(card.delta);
          return (
            <div key={card.key} className='col-12 col-sm-6 col-xl-3'>
              <Card specialPadding='p-3'>
                <div className='d-flex align-items-center gap-2 mb-3'>
                  <div className='rounded-2 bg-primary-soft p-2 d-flex align-items-center justify-content-center'>
                    <SvgIcon
                      name={card.icon}
                      size='20px'
                      stroke={
                        card.icon === 'whatsapp'
                          ? undefined
                          : 'var(--font-light)'
                      }
                      fill={
                        card.icon === 'whatsapp'
                          ? 'var(--font-light)'
                          : undefined
                      }
                    />
                  </div>
                  <span className='caption font-light'>{card.label}</span>
                </div>
                <div className='h4 mb-1'>{formatNumber(card.value)}</div>
                <p className={`caption mb-0 ${delta.colorClass}`}>
                  {delta.prefix} {delta.amount} {t('payments_this_month')}
                </p>
              </Card>
            </div>
          );
        })}
      </div>

      <div className='row'>
        <div className='col-12 mb-3'>
          <Card>
            <div className='d-flex justify-content-between align-items-center mb-3'>
              <div>
                <h3 className='mb-1'>{t('payments_methods_title')}</h3>
                <p className='caption font-light mb-0'>
                  {t('payments_methods_subtitle')}
                </p>
              </div>
              <Button
                type='button'
                className='d-flex align-items-center gap-2'
                variant='primary'
                iconStart='plusLg'
                label={t('payments_add_method')}
                size='sm'
                onClick={handleAddMethod}
                disabled={!canAddPaymentMethod}
              />
            </div>

            <div className='d-flex flex-column gap-3'>
              {paymentMethods.map((method) => {
                const methodStatus = normalizeMethodStatus(method.status);
                return (
                  <div
                    key={method.id}
                    className='border rounded-3 p-3 payment-method-card d-flex flex-column gap-3'
                  >
                    <div className='d-flex justify-content-between align-items-center'>
                      <div className='d-flex align-items-center gap-2'>
                        <div className='rounded-2 bg-primary-soft p-2 d-flex align-items-center justify-content-center'>
                          <SvgIcon name='creditCard' size='20px' />
                        </div>
                        <div className='d-flex flex-column'>
                          <span className='body-2 fw-semibold'>
                            {method.brand}
                          </span>
                          <span className='caption font-light'>
                            **** **** **** {method.last4}
                          </span>
                          <span className='caption font-light'>
                            {t('payments_holder_prefix')}: {method.holder}
                          </span>
                        </div>
                      </div>
                      {method.default && (
                        <span className='badge bg-primary-soft text-primary-500'>
                          {t('payments_default_badge')}
                        </span>
                      )}
                    </div>

                    <div className='d-flex flex-wrap justify-content-between align-items-center gap-3'>
                      <div className='d-flex flex-wrap gap-3'>
                        <span className='caption font-light'>
                          {t('payments_expires_prefix')}: {method.expires}
                        </span>
                        <span className='caption font-light'>
                          - {method.network}
                        </span>
                        <span
                          className={`badge ${
                            {
                              active: 'bg-success-soft text-success-700',
                              inactive: 'bg-error-soft text-error-700',
                            }[methodStatus]
                          }`}
                        >
                          {methodStatus === 'active'
                            ? t('payments_status_active')
                            : t('payments_status_inactive')}
                        </span>
                      </div>

                      <div className='d-flex align-items-center gap-3'>
                        {!method.default && (
                          <Button
                            type='button'
                            variant='link'
                            label={t('payments_set_default')}
                            size='sm'
                            disabled={!canSetDefaultPayment}
                          />
                        )}
                        <IconButton
                          icon='pencil'
                          size='sm'
                          ariaLabel={t('payments_edit_method_aria')}
                          onClick={() => handleEditMethod(method)}
                          iconStroke='var(--primary-500)'
                          disabled={!canEditPaymentMethod}
                        />
                        <IconButton
                          icon='trash'
                          size='sm'
                          ariaLabel={t('payments_delete_method_aria')}
                          iconStroke='var(--error-500)'
                          onClick={() => {
                            setSelectedMethod(method);
                            setShowDelete(true);
                          }}
                          disabled={!canDeletePaymentMethod}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        </div>

        <div className='col-12'>
          <Card>
            <div className='d-flex justify-content-between align-items-center mb-3'>
              <div>
                <h3 className='mb-1'>{t('payments_subscription_title')}</h3>
                <p className='caption font-light mb-0'>
                  {t('payments_subscription_subtitle')}
                </p>
              </div>
            </div>

            <div className='row mb-4 g-3'>
              <div className='col-12 col-sm-6'>
                <p className='caption font-light mb-1'>
                  {t('payments_plan_current')}
                </p>
                <p className='body-2 mb-0 fw-semibold'>
                  {subscription.planName}
                </p>
              </div>

              <div className='col-12 col-sm-6'>
                <p className='caption font-light mb-1'>{t('payments_price')}</p>
                <p className='body-2 mb-0 fw-semibold'>
                  {formatCurrency(subscription.price)} {subscription.currency}
                </p>
              </div>

              <div className='col-12 col-sm-6'>
                <p className='caption font-light mb-1'>
                  {t('payments_next_charge')}
                </p>
                <p className='body-2 mb-0 fw-semibold'>
                  {subscription.nextCharge}
                </p>
              </div>

              <div className='col-12 col-sm-6'>
                <p className='caption font-light mb-1'>
                  {t('payments_billing_period')}
                </p>
                <p className='body-2 mb-0 fw-semibold'>
                  {subscription.billingPeriod}
                </p>
              </div>

              <div className='col-12 col-sm-6'>
                <p className='caption font-light mb-1'>
                  {t('payments_billing_email')}
                </p>
                <p className='body-2 mb-0 fw-semibold'>
                  {subscription.billingEmail}
                </p>
              </div>

              <div className='col-12 col-sm-6'>
                <p className='caption font-light mb-1'>
                  {t('payments_status')}
                </p>
                <span className='badge bg-success-soft text-success-700'>
                  {t('payments_status_active')}
                </span>
              </div>
            </div>

            <div className='d-flex flex-wrap gap-2 justify-content-end'>
              <Button
                type='button'
                variant='secondary'
                label={t('payments_change_plan')}
                size='sm'
                disabled={!canChangePlan}
              />
              <Button
                type='button'
                variant='link'
                label={t('payments_cancel_subscription')}
                size='sm'
                disabled={!canCancelSubscription}
              />
            </div>
          </Card>
        </div>
      </div>

      <CreatePaymentMethodModal
        Modal={Modal}
        Input={Input}
        Dropdown={Dropdown}
        show={showMethodModal}
        mode={mode}
        initialData={selectedMethod}
        onClose={() => {
          setShowMethodModal(false);
          setSelectedMethod(null);
          setMode('auto');
        }}
        onSave={handleSaveMethod}
      />

      <ConfirmModal
        Modal={Modal}
        show={showDelete}
        title={t('payments_confirm_delete_title')}
        message={
          selectedMethod
            ? t('payments_confirm_delete_message', {
                brand: selectedMethod.brand,
                last4: selectedMethod.last4,
              })
            : t('payments_confirm_delete_fallback')
        }
        confirmLabel={t('payments_confirm_delete_button')}
        cancelLabel={t('payments_cancel')}
        confirmVariant='danger'
        onConfirm={handleDeleteMethod}
        onClose={() => setShowDelete(false)}
      />
    </div>
  );
}
